/**
 * A checklist checks itself when it comes on screen: each row's text fades in and lifts into place,
 * and a third of the way in its tick is drawn with one stroke, from the short leg up the long one.
 * The rows go in a ripple from the top left: with the columns side by side a row starts two beats
 * after the row at its left, so the diagonal reads across the columns; with the columns stacked (a
 * phone) the rows go top to bottom. The footer parts (a button, a note) follow, one beat after the
 * last row and half a beat apart.
 *
 * The recipe is the one the team picked in the reveal lab: a second per row, 100 ms between rows, a
 * 300 ms lead-in so the reader has settled on the heading, ease-out-expo for the text and
 * ease-out-quint for the stroke. The stroke is a dash offset, the one main-thread property here, and
 * its animation exists only for the draw (modules/motion/stroke.js). The pending state holds the text and the ticks hidden until
 * the list plays (modules/motion/once.js); the footer is hidden as a whole, and once the state
 * releases it, each part's first keyframe holds it until its turn. The resting markup is the
 * finished list.
 */
import {EASING_CURVES} from '../modules/motion/easing.js';
import {playOnce} from '../modules/motion/once.js';
import {drawStroke} from '../modules/motion/stroke.js';

const SELECTOR = Object.freeze({
    root: '.js-checklist',
    column: '.js-checklistColumn',
    text: '.js-checklistText',
    tick: '.js-checklistTick path',
    footer: '.js-checklistFooter',
});

/** Milliseconds. */
const ROW = Object.freeze({duration: 1000, stagger: 100, leadIn: 300});
/** The tick's stroke, as shares of the row's duration: it starts once the text is well on its way. */
const TICK = Object.freeze({start: 0.35, span: 0.55});
/** Beats a column waits behind the column at its left, so the ripple runs on the diagonal. */
const COLUMN_LAG = 2;
/** Beats between the footer parts; the first comes a full beat after the last row. */
const FOOTER_STEP = 0.5;
/** Pixels a footer part lifts as it enters. */
const FOOTER_LIFT = 16;
const EASE = Object.freeze({
    text: EASING_CURVES['ease-out-expo'],
    draw: EASING_CURVES['ease-out-quint'],
});

/** Hold the first keyframe through the delay; the natural styles take over at the end. */
const FILL = 'backwards';

/**
 * The beat each row starts on. Side by side, a row's beat is its index plus the lag of its column;
 * stacked, the columns follow one another.
 * @param {HTMLElement[]} columns
 * @returns {Map<Element, number>}
 */
function rowBeats(columns) {
    const top = columns[0]?.getBoundingClientRect().top;
    const sideBySide = columns.length > 1 && columns.every((column) => column.getBoundingClientRect().top === top);
    /** @type {Map<Element, number>} */
    const beats = new Map();
    let rowsAbove = 0;
    columns.forEach((column, columnIndex) => {
        [...column.children].forEach((row, rowIndex) => {
            beats.set(row, sideBySide ? rowIndex + columnIndex * COLUMN_LAG : rowsAbove + rowIndex);
        });
        rowsAbove += column.children.length;
    });

    return beats;
}

/**
 * The element of a footer part that has a box. A part with `display: contents` (the button
 * component's wrapper) paints nothing itself, so an entrance on it would show nothing.
 * @param {Element} part
 * @returns {Element}
 */
function boxOf(part) {
    if (getComputedStyle(part).display !== 'contents') {
        return part;
    }
    if (part.firstElementChild === null) {
        throw new Error('checklist: a footer part without a box has nothing to animate.');
    }

    return boxOf(part.firstElementChild);
}

/**
 * @param {HTMLElement} root
 * @param {import('../modules/motion/run.js').Run} run
 */
function start(root, run) {
    const columns = [...root.querySelectorAll(SELECTOR.column)].filter((column) => column instanceof HTMLElement);
    const beats = rowBeats(columns);
    let lastBeat = 0;
    for (const [row, beat] of beats) {
        lastBeat = Math.max(lastBeat, beat);
        const text = row.querySelector(SELECTOR.text);
        const tick = row.querySelector(SELECTOR.tick);
        if (!(text instanceof HTMLElement) || !(tick instanceof SVGElement)) {
            throw new Error('checklist: every row needs its text and its tick.');
        }
        const delay = ROW.leadIn + beat * ROW.stagger;
        run.add(text.animate([{opacity: 0, translate: '0 10px'}, {opacity: 1, translate: '0 0'}], {delay, duration: ROW.duration, easing: EASE.text, fill: FILL}));
        // The catalog drawing has no pathLength and runs from the long leg to the short one.
        tick.setAttribute('pathLength', '1');
        tick.setAttribute('stroke-dasharray', '1 1');
        run.add(drawStroke(tick, {delay: delay + ROW.duration * TICK.start, duration: ROW.duration * TICK.span, easing: EASE.draw}, 'end'));
    }
    const footer = root.querySelector(SELECTOR.footer);
    [...(footer?.children ?? [])].forEach((part, index) => {
        const delay = ROW.leadIn + (lastBeat + 1 + index * FOOTER_STEP) * ROW.stagger;
        run.add(boxOf(part).animate([{opacity: 0, translate: `0 ${FOOTER_LIFT}px`}, {opacity: 1, translate: '0 0'}], {delay, duration: ROW.duration, easing: EASE.text, fill: FILL}));
    });
}

/**
 * @param {HTMLElement} root
 */
export function initializeChecklist(root) {
    playOnce(root, (run) => start(root, run));
}

/**
 * Initialize every checklist that is not initialized yet. Safe to call after content swaps.
 */
export function initializeChecklists() {
    for (const root of document.querySelectorAll(SELECTOR.root)) {
        if (root instanceof HTMLElement) {
            initializeChecklist(root);
        }
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeChecklists);
} else {
    initializeChecklists();
}
document.addEventListener('htmx:load', initializeChecklists);
